import { Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import type { ActionItemRow, ActionItemStatus } from "@/hooks/use-action-items";
import { usePatchActionItem } from "@/hooks/use-action-items";
import { useStakeholders } from "@/hooks/use-stakeholders";
import { cn } from "@/lib/utils";

interface FollowupDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  solutionId: string;
  item: ActionItemRow | null;
}

const STATUS_OPTIONS: ReadonlyArray<{ id: ActionItemStatus; label: string }> = [
  { id: "pending", label: "Pending" },
  { id: "snoozed", label: "Snoozed" },
  { id: "sent", label: "Sent" },
  { id: "done", label: "Done" },
  { id: "dismissed", label: "Dismissed" },
];

export function FollowupDetailSheet({
  open,
  onOpenChange,
  solutionId,
  item,
}: FollowupDetailSheetProps) {
  const stakeholders = useStakeholders(solutionId);
  const patch = usePatchActionItem(solutionId);

  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [contact, setContact] = useState("");
  const [pmProject, setPmProject] = useState("");
  const [pmType, setPmType] = useState("");
  const [pmEpic, setPmEpic] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open && item) {
      setTitle(item.title);
      setDueDate(item.dueDate ? item.dueDate.slice(0, 10) : "");
      setContact(item.sourceSpeaker?.id ?? "");
      setPmProject(item.pmTarget?.project ?? "");
      setPmType(item.pmTarget?.type ?? "");
      setPmEpic(item.pmTarget?.epic ?? "");
      // Notes are local to this sheet for v1 — kept in the updated quote field on save.
      setNotes(item.quote ?? "");
    }
  }, [open, item]);

  if (!item) return null;

  function buildPatch(includeStatus?: ActionItemStatus) {
    if (!item) return null;
    return {
      title: title.trim() && title.trim() !== item.title ? title.trim() : undefined,
      dueDate:
        dueDate !== (item.dueDate?.slice(0, 10) ?? "")
          ? dueDate
            ? new Date(`${dueDate}T00:00:00Z`).toISOString()
            : null
          : undefined,
      pmTarget:
        pmProject || pmType || pmEpic
          ? { project: pmProject, type: pmType, epic: pmEpic || null }
          : item.pmTarget
            ? null
            : undefined,
      status: includeStatus,
    };
  }

  function save() {
    if (!item) return;
    const body = buildPatch();
    if (!body) return;
    patch.mutate(
      { id: item.id, patch: body },
      {
        onSuccess: () => {
          toast.success("Saved");
          onOpenChange(false);
        },
      },
    );
  }

  function pushToDeliver() {
    if (!item) return;
    const body = buildPatch("sent");
    if (!body) return;
    patch.mutate(
      { id: item.id, patch: body },
      {
        onSuccess: () => {
          toast.success("Pushed to Deliver");
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] flex flex-col">
        <SheetHeader className="border-b border-[var(--color-border)]">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Follow-up · {item.kind}
          </div>
          <SheetTitle className="font-display text-lg leading-snug mt-1">
            Edit before pushing
          </SheetTitle>
          <SheetDescription className="sr-only">
            Add details, contacts, and notes before pushing to Deliver.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <Field label="Title" required>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-sm"
              autoFocus
            />
          </Field>

          {item.source?.name && (
            <div className="rounded-md border border-nymbl-sky bg-nymbl-sky/40 px-3 py-2 text-xs flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                Source
              </span>
              <span className="text-foreground/80 truncate">{item.source.name}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Due date">
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="h-9 text-sm"
              />
            </Field>

            <Field label="Status">
              <Select
                value={item.status}
                onValueChange={(v) =>
                  patch.mutate({ id: item.id, patch: { status: v as ActionItemStatus } })
                }
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Primary contact">
            {stakeholders.isLoading ? (
              <div className="h-9 w-full rounded-md bg-muted animate-pulse" />
            ) : (
              <Select value={contact} onValueChange={setContact}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Pick a contact…" />
                </SelectTrigger>
                <SelectContent>
                  {stakeholders.data?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} <span className="text-muted-foreground">— {p.role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <fieldset className={cn("rounded-md border border-[var(--color-border)] p-3 space-y-3")}>
            <legend className="px-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              Deliver target
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input
                value={pmProject}
                onChange={(e) => setPmProject(e.target.value)}
                placeholder="Project (e.g. DYL)"
                className="h-9 text-sm"
              />
              <Input
                value={pmType}
                onChange={(e) => setPmType(e.target.value)}
                placeholder="Type (e.g. Task)"
                className="h-9 text-sm"
              />
              <Input
                value={pmEpic}
                onChange={(e) => setPmEpic(e.target.value)}
                placeholder="Epic (optional)"
                className="h-9 text-sm"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              When pushed, the task lands in this project/type/epic in your PM tool.
            </p>
          </fieldset>

          <Field label="Notes for the assignee">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="text-sm"
              placeholder="Context, links, what's already known…"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Stored on the action item; carried over to Deliver in the description.
            </p>
          </Field>
        </div>

        <SheetFooter className="border-t border-[var(--color-border)] flex flex-row sm:justify-between gap-2 p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={save} disabled={patch.isPending}>
              Save
            </Button>
            <Button onClick={pushToDeliver} disabled={patch.isPending}>
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Push to Deliver
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </div>
      {children}
    </div>
  );
}
